import { useMemo } from 'react'
import { UnheadProvider } from '@unhead/react/client'
import type { FMPProps, FormStepResult } from './types'
import QuestionnaireWrapper from './questionnaire/QuestionnaireWrapper'
import FMPStoreProvider from './store/FMPStoreProvider'
import ShadowDomWrapper from '@strata-monorepo/shared-ui/utils/ShadowDom'
import { ThemeContext, useTheme } from '@strata-monorepo/shared-ui/utils/context/ThemeContext'
import DeferredCohesionScript from '@strata-monorepo/shared-ui/utils/Cohesion/DeferredCohesionScript'
import { DomainEnum } from '@strata-monorepo/shared-types/src/Domain'
import { MetricsProvider } from './metrics/MetricsProvider'
import { useMetrics } from './hooks/useMetrics'
import { useIncense } from './hooks/useIncense'
import ApiProvider from '@strata-monorepo/shared-ui/utils/api/ApiProvider'
import { useStoreState } from './store/useStore'
import { CohesionProvider } from '@redventures/cohesion-utils-react'
import { withHomeInfoHandoff } from './utils/homeInfoHandoff'

// Stable across renders, so answers derived from it don't re-run the questionnaire's seeding effect
const NO_DEFAULT_FORM_RESULTS: FormStepResult = {}

/**
 * Questionnaire component for the find-my-plan library
 *
 * This is a simplified version of the questionnaire that will be expanded
 * in future iterations to include more functionality.
 */
export function FMP({
  onComplete = (_results, _metadata) => {
    console.log('FMP completed!')
  },
  defaultFormResults = NO_DEFAULT_FORM_RESULTS,
  siteDomain = DomainEnum.CTXP,
  onErrorCta = () => {
    console.log('FMP error!')
  },
  fuseConfigs = [],
}: FMPProps = {}) {
  const { metricsClient, error } = useMetrics()

  const theme = useTheme()
  const formResults = useMemo(() => withHomeInfoHandoff(defaultFormResults), [defaultFormResults])

  if (error) {
    console.warn('[FMP] Metrics initialization error')
  }

  return (
    <ThemeContext.Provider value='theme-ctxp'>
      <ShadowDomWrapper>
        <UnheadProvider>
          <DeferredCohesionScript>
            <CohesionProvider>
              <MetricsProvider client={metricsClient}>
                <FMPStoreProvider
                  initialFormState={formResults}
                  siteDomain={siteDomain}
                >
                  <ApiProvider
                    getIncense={useIncense}
                    UseStoreState={useStoreState}
                  >
                    <div className={`${theme} strata:font-primary`}>
                      <QuestionnaireWrapper
                        defaultFormResults={formResults}
                        fuseConfigs={fuseConfigs}
                        onComplete={onComplete}
                        onErrorCta={onErrorCta}
                      />
                    </div>
                  </ApiProvider>
                </FMPStoreProvider>
              </MetricsProvider>
            </CohesionProvider>
          </DeferredCohesionScript>
        </UnheadProvider>
      </ShadowDomWrapper>
    </ThemeContext.Provider>
  )
}
